import apiCall from "./apiCall";

// Backend Voucher model stores type as payment/receipt/journal (DB enum),
// but the whole frontend uses PV/RV/JV as the canonical value. Translate
// at this boundary so callers never have to know about the backend values.
const VOUCHER_TYPE_TO_BACKEND = { PV: "payment", RV: "receipt", JV: "journal" };
const VOUCHER_TYPE_TO_UI = { payment: "PV", receipt: "RV", journal: "JV" };

const voucherTypeToUI = (v) => {
  if (v && typeof v === "object") v.type = VOUCHER_TYPE_TO_UI[v.type] || v.type;
  return v;
};

const ajaxAccounting = {
  // ─── FINANCIAL YEAR ─────────────────────────────────────────────────────
  listFinancialYears: (data) => apiCall("accounting/financial-year/list", data),
  getCurrentFinancialYear: () =>
    apiCall("accounting/financial-year/current", {}),
  createFinancialYear: (data) =>
    apiCall("accounting/financial-year/create", data),
  closeFinancialYear: (data) =>
    apiCall("accounting/financial-year/close", data),
  lockFinancialYear: (data) => apiCall("accounting/financial-year/lock", data),
  unlockFinancialYear: (data) =>
    apiCall("accounting/financial-year/unlock", data),

  // ─── CONFIG ──────────────────────────────────────────────────────────────
  getConfig: (data) => apiCall("accounting/config/get", data),
  setConfig: (data) => apiCall("accounting/config/set", data),
  listConfig: () => apiCall("accounting/config/list", {}),
  listSystemConfig: () => apiCall("accounting/system-config/list", {}),
  setSystemConfig: (data) => apiCall("accounting/system-config/set", data),
  listCurrencies: () => apiCall("accounting/currency/list", {}),
  addCurrency: (data) => apiCall("accounting/currency/add", data),
  listTaxes: () => apiCall("accounting/tax/list", {}),
  addTax: (data) => apiCall("accounting/tax/add", data),

  // ─── CHART OF ACCOUNTS ───────────────────────────────────────────────────
  listAccounts: (data) => apiCall("accounting/accounts/list", data || {}),
  getAccountTree: () => apiCall("accounting/accounts/tree", {}),
  createAccount: (data) => apiCall("accounting/accounts/create", data),
  updateAccount: (data) => apiCall("accounting/accounts/update", data),
  getAccount: (data) => apiCall("accounting/accounts/get", data),
  getAccountBalance: (data) => apiCall("accounting/accounts/balance", data),
  seedAccounts: (data) => apiCall("accounting/accounts/seed", data),

  // ─── CONTACTS ────────────────────────────────────────────────────────────
  listContacts: (data) => apiCall("accounting/contacts/list", data || {}),
  createContact: (data) => apiCall("accounting/contacts/create", data),
  updateContact: (data) => apiCall("accounting/contacts/update", data),
  getContactStatement: (data) => apiCall("accounting/contacts/statement", data),

  // ─── PROJECTS ────────────────────────────────────────────────────────────
  listProjects: (data) => apiCall("accounting/projects/list", data || {}),
  createProject: (data) => apiCall("accounting/projects/create", data),
  closeProject: (data) => apiCall("accounting/projects/close", data),

  // ─── BORROWED CAPITAL ────────────────────────────────────────────────────
  listLoans: (data) => apiCall("accounting/loans/list", data || {}),
  getLoan: (data) => apiCall("accounting/loans/get", data),
  loanSummary: (data) => apiCall("accounting/loans/summary", data || {}),
  borrowCapital: (data) => apiCall("accounting/loans/borrow", data),
  repayLoan: (data) => apiCall("accounting/loans/repay", data),
  getProjectBudgetVsActual: (data) =>
    apiCall("accounting/projects/budget-vs-actual", data),

  // ─── JOURNAL ENTRIES ─────────────────────────────────────────────────────
  listJournals: (data) => apiCall("accounting/journal/list", data || {}),
  createJournal: (data) => apiCall("accounting/journal/create", data),
  postJournal: (data) => apiCall("accounting/journal/post", data),
  createAndPostJournal: (data) =>
    apiCall("accounting/journal/create-and-post", data),
  reverseJournal: (data) => apiCall("accounting/journal/reverse", data),
  updateJournal: (data) => apiCall("accounting/journal/update", data),
  deleteJournal: (data) => apiCall("accounting/journal/delete", data),
  getJournal: (data) => apiCall("accounting/journal/get", data),

  // ─── LEDGER ──────────────────────────────────────────────────────────────
  getLedger: (data) => apiCall("accounting/ledger/account", data),
  getTrialBalance: (data) => apiCall("accounting/ledger/trial-balance", data),

  // ─── INVOICES ────────────────────────────────────────────────────────────
  listInvoices: (data) => apiCall("accounting/invoices/list", data || {}),
  createInvoice: (data) => apiCall("accounting/invoices/create", data),
  approveInvoice: (data) => apiCall("accounting/invoices/approve", data),
  payInvoice: (data) => apiCall("accounting/invoices/pay", data),
  voidInvoice: (data) => apiCall("accounting/invoices/void", data),
  getInvoice: (data) => apiCall("accounting/invoices/get", data),
  getInvoiceAging: (data) => apiCall("accounting/invoices/aging", data),

  // ─── VOUCHERS ────────────────────────────────────────────────────────────
  listVouchers: async (data) => {
    const payload = { ...(data || {}) };
    if (payload.type) payload.type = VOUCHER_TYPE_TO_BACKEND[payload.type] || payload.type;
    const res = await apiCall("accounting/vouchers/list", payload);
    if (res.status === "OK") {
      const list = Array.isArray(res.details) ? res.details : res.details?.list;
      if (Array.isArray(list)) list.forEach(voucherTypeToUI);
    }
    return res;
  },
  createVoucher: async (data) => {
    const payload = { ...data, type: VOUCHER_TYPE_TO_BACKEND[data.type] || data.type };
    const res = await apiCall("accounting/vouchers/create", payload);
    if (res.status === "OK") voucherTypeToUI(res.details);
    return res;
  },
  updateVoucher: (data) => apiCall("accounting/vouchers/update", data),
  deleteVoucher: (data) => apiCall("accounting/vouchers/delete", data),
  approveVoucher: async (data) => {
    const res = await apiCall("accounting/vouchers/approve", data);
    if (res.status === "OK") voucherTypeToUI(res.details);
    return res;
  },
  getVoucher: async (data) => {
    const res = await apiCall("accounting/vouchers/get", data);
    if (res.status === "OK") voucherTypeToUI(res.details);
    return res;
  },

  // ─── BUDGET ──────────────────────────────────────────────────────────────
  listBudgets: (data) => apiCall("accounting/budget/list", data || {}),
  createBudget: (data) => apiCall("accounting/budget/create", data),
  getBudget: (data) => apiCall("accounting/budget/get", data),
  setBudgetLine: (data) => apiCall("accounting/budget/set-line", data),
  getBudgetVsActual: (data) => apiCall("accounting/budget/vs-actual", data),

  // ─── REPORTS ─────────────────────────────────────────────────────────────
  getIncomeStatement: (data) =>
    apiCall("accounting/reports/income-statement", data),
  getBalanceSheet: (data) => apiCall("accounting/reports/balance-sheet", data),
  getCashFlow: (data) => apiCall("accounting/reports/cash-flow", data),

  // ─── DASHBOARD ───────────────────────────────────────────────────────────
  // One call for the whole dashboard — summary, charts and the industry
  // blocks. Replaces getDashboardSummary + the eight chart calls below it.
  getDashboardOverview: (data) => apiCall("accounting/dashboard/overview", data),
  getDashboardSummary: (data) => apiCall("accounting/dashboard/summary", data),
  getRevenueVsExpenses: (data) =>
    apiCall("accounting/charts/revenue-vs-expenses", data),
  getNetProfitTrend: (data) =>
    apiCall("accounting/charts/net-profit-trend", data),
  getRevenueBreakdown: (data) =>
    apiCall("accounting/charts/revenue-breakdown", data),
  getCashFlowMonthly: (data) =>
    apiCall("accounting/charts/cash-flow-monthly", data),
  getArAging: (data) => apiCall("accounting/charts/ar-aging", data),
  getApAging: (data) => apiCall("accounting/charts/ap-aging", data),
  getInvoiceStatus: (data) => apiCall("accounting/charts/invoice-status", data),
  getTopCustomers: (data) => apiCall("accounting/charts/top-customers", data),

  // ─── LEDGER APPROVALS ────────────────────────────────────────────────────
  listApprovals: (data) => apiCall("accounting/approvals/list", data || {}),
  pendingApprovals: (data) => apiCall("accounting/approvals/pending-for-me", data || {}),
  myApprovalSubmissions: (data) => apiCall("accounting/approvals/my-submissions", data || {}),
  getApprovalRequest: (data) => apiCall("accounting/approvals/request/get", data),
  submitApproval: (data) => apiCall("accounting/approvals/submit", data),
  approveRequest: (data) => apiCall("accounting/approvals/approve", data),
  rejectRequest: (data) => apiCall("accounting/approvals/reject", data),
  delegateRequest: (data) => apiCall("accounting/approvals/delegate", data),
  listWorkflows: (data) => apiCall("accounting/approvals/workflow/list", data || {}),
  createWorkflow: (data) => apiCall("accounting/approvals/workflow/create", data),
  addWorkflowLevel: (data) => apiCall("accounting/approvals/workflow/add-level", data),

  // ─── MANAGERIAL ACCOUNTING ───────────────────────────────────────────────
  mgKpiRatios: (data) => apiCall("accounting/managerial/kpi-ratios", data),
  mgVariance: (data) => apiCall("accounting/managerial/variance", data),
  mgContributionMargin: (data) => apiCall("accounting/managerial/contribution-margin", data),
  mgBreakEven: (data) => apiCall("accounting/managerial/break-even", data),
  mgSegmentPl: (data) => apiCall("accounting/managerial/segment-pl", data),
  mgCostReport: (data) => apiCall("accounting/managerial/cost/report", data),
  mgOverheadRate: (data) => apiCall("accounting/managerial/overhead-rate", data),

  // ─── PERIOD REPORTS ──────────────────────────────────────────────────────
  periodIncomeStatement: (data) => apiCall("accounting/period/income-statement", data),
  periodBalanceSheet: (data) => apiCall("accounting/period/balance-sheet", data),
  periodTrialBalance: (data) => apiCall("accounting/period/trial-balance", data),
  periodCashFlow: (data) => apiCall("accounting/period/cash-flow", data),
  periodQuarterlyIncome: (data) => apiCall("accounting/period/quarterly-income", data),
  periodMonthlyIncome: (data) => apiCall("accounting/period/monthly-income", data),
  periodYearOverYear: (data) => apiCall("accounting/period/year-over-year", data),

  // ─── SUBSCRIPTIONS ───────────────────────────────────────────────────────
  subListPlans: (data) => apiCall("accounting/subscriptions/plans/list", data || {}),
  subCreatePlan: (data) => apiCall("accounting/subscriptions/plans/create", data),
  subListSubscriptions: (data) => apiCall("accounting/subscriptions/list", data || {}),
  subscribe: (data) => apiCall("accounting/subscriptions/subscribe", data),
  subCancel: (data) => apiCall("accounting/subscriptions/cancel", data),
  subMrr: (data) => apiCall("accounting/subscriptions/mrr", data || {}),
  subChurn: (data) => apiCall("accounting/subscriptions/churn", data || {}),
  subRevenueByMonth: (data) => apiCall("accounting/subscriptions/revenue-by-month", data || {}),
  subUpcomingRenewals: (data) => apiCall("accounting/subscriptions/upcoming-renewals", data || {}),

  // ─── BANK RECONCILIATION ─────────────────────────────────────────────────
  listBankTransactions: (data) =>
    apiCall("accounting/bank/transactions/list", data || {}),
  reconcileTransaction: (data) => apiCall("accounting/bank/reconcile", data),
  unreconcileTransaction: (data) => apiCall("accounting/bank/unreconcile", data),
  getReconciliationReport: (data) =>
    apiCall("accounting/bank/reconciliation-report", data),

  // ─── AUDIT / RECORDS ─────────────────────────────────────────────────────
  listAuditTrail: (data) => apiCall("accounting/audit/list", data || {}),
  listRecords: (data) => apiCall("accounting/records/list", data || {}),

  // ─── USER (permissions) ──────────────────────────────────────────────────
  getUserPermissionCodes: (data) =>
    apiCall("system/user/permission-codes", data),

  // ─── YEAR CLOSE ────────────────────────────────────────────────────────────
  getYearCloseChecklist: (data) =>
    apiCall("accounting/year-close/checklist", data),
  postYearCloseEntries: (data) =>
    apiCall("accounting/year-close/post-closing-entries", data),
  closeYear: (data) => apiCall("accounting/year-close/close", data),
  migrateBalances: (data) =>
    apiCall("accounting/year-close/migrate-balances", data),
  importOpeningBalances: (data) =>
    apiCall("accounting/year-close/import-balances", data),
  getYearCloseLog: (data) => apiCall("accounting/year-close/log", data),
};

export default ajaxAccounting;

// ─── YEAR CLOSE ──────────────────────────────────────────────────────────────
const ajaxYearClose = {
  getYearCloseChecklist: (data) =>
    apiCall("accounting/year-close/checklist", data),
  postYearCloseEntries: (data) =>
    apiCall("accounting/year-close/post-closing-entries", data),
  closeYear: (data) => apiCall("accounting/year-close/close", data),
  migrateBalances: (data) =>
    apiCall("accounting/year-close/migrate-balances", data),
  migrateInvoices: (data) =>
    apiCall("accounting/year-close/migrate-invoices", data),
  importOpeningBalances: (data) =>
    apiCall("accounting/year-close/import-balances", data),
  getYearCloseLog: (data) => apiCall("accounting/year-close/log", data),
};

// ─── PAYROLL ─────────────────────────────────────────────────────────────────
const ajaxPayroll = {
  createEmployee: (data) =>
    apiCall("accounting/payroll/employees/create", data),
  updateEmployee: (data) =>
    apiCall("accounting/payroll/employees/update", data),
  terminateEmployee: (data) =>
    apiCall("accounting/payroll/employees/terminate", data),
  getEmployee: (data) => apiCall("accounting/payroll/employees/get", data),
  listEmployees: (data) => apiCall("accounting/payroll/employees/list", data),
  // Staff are already on the system as users; these two let payroll take them
  // on without anybody being typed in twice.
  availableUsersForPayroll: (data) =>
    apiCall("accounting/payroll/employees/available-users", data || {}),
  addEmployeesFromUsers: (data) =>
    apiCall("accounting/payroll/employees/add-from-users", data),
  addAllowance: (data) =>
    apiCall("accounting/payroll/employees/add-allowance", data),
  removeAllowance: (data) =>
    apiCall("accounting/payroll/employees/remove-allowance", data),
  addDeduction: (data) =>
    apiCall("accounting/payroll/employees/add-deduction", data),
  removeDeduction: (data) =>
    apiCall("accounting/payroll/employees/remove-deduction", data),
  processPayroll: (data) => apiCall("accounting/payroll/run/process", data),
  approvePayroll: (data) => apiCall("accounting/payroll/run/approve", data),
  listPayrollRuns: (data) => apiCall("accounting/payroll/run/list", data),
  getPayrollRun: (data) => apiCall("accounting/payroll/run/get", data),
  getPayslips: (data) => apiCall("accounting/payroll/run/payslips", data),
  getPayrollSummary: (data) => apiCall("accounting/payroll/run/summary", data),
  getPayslip: (data) => apiCall("accounting/payroll/payslip/get", data),
  getPayslipByEmployee: (data) =>
    apiCall("accounting/payroll/payslip/by-employee", data),
  // Single employee's payslip.
  previewPayroll: (data) => apiCall("accounting/payroll/preview", data),
  // The whole run for a period, computed but not written.
  previewPayrollRun: (data) => apiCall("accounting/payroll/run/preview", data),
  issueAdvance: (data) => apiCall("accounting/payroll/advances/issue", data),
  listAdvances: (data) => apiCall("accounting/payroll/advances/list", data),
  getAdvance: (data) => apiCall("accounting/payroll/advances/get", data),
  cancelAdvance: (data) => apiCall("accounting/payroll/advances/cancel", data),
  outstandingAdvances: (data) => apiCall("accounting/payroll/advances/outstanding", data),
};

// ─── SCHOOL FEES ─────────────────────────────────────────────────────────────
const ajaxFees = {
  // Every student in one stream with billed / paid / balance and a risk flag.
  // Marks a student as sponsored; applies to tuition on every future bill.

  // Where the school is in its year, and how students move through it.
};

// ─── TAX FILINGS ─────────────────────────────────────────────────────────────
const ajaxTax = {
  getPayeMonthly: (data) => apiCall("accounting/tax/paye-monthly", data),
  getPayeAnnual: (data) => apiCall("accounting/tax/paye-annual-p10", data),
  getP9: (data) => apiCall("accounting/tax/p9", data),
  getVatReturn: (data) => apiCall("accounting/tax/vat-return", data),
  getWhtReturn: (data) => apiCall("accounting/tax/wht-return", data),
  getNssfSchedule: (data) => apiCall("accounting/tax/nssf-schedule", data),
  listTaxFilings: (data) => apiCall("accounting/tax/filings/list", data),
  getTaxFiling: (data) => apiCall("accounting/tax/filings/get", data),
  submitTaxFiling: (data) => apiCall("accounting/tax/filings/submit", data),
};

// ─── MIGRATION ASSISTANT ─────────────────────────────────────────────────────
const ajaxMigration = {
  importOpeningBalances: (data) =>
    apiCall("accounting/year-close/import-balances", data),
  migrateBalances: (data) =>
    apiCall("accounting/year-close/migrate-balances", data),
  migrateInvoices: (data) =>
    apiCall("accounting/year-close/migrate-invoices", data),
};

// ─── ITEM CATALOGS (Products/Services for sales, Purchase Items for purchases) ───
const ajaxItems = {
  listSalesItems: (data) => apiCall("accounting/items/sales/list", data || {}),
  createSalesItem: (data) => apiCall("accounting/items/sales/create", data),
  updateSalesItem: (data) => apiCall("accounting/items/sales/update", data),
  getSalesItem: (data) => apiCall("accounting/items/sales/get", data),
  adjustSalesItemStock: (data) => apiCall("accounting/items/sales/adjust-stock", data),
  getLowStockSalesItems: () => apiCall("accounting/items/sales/low-stock", {}),

  listPurchaseItems: (data) => apiCall("accounting/items/purchase/list", data || {}),
  createPurchaseItem: (data) => apiCall("accounting/items/purchase/create", data),
  updatePurchaseItem: (data) => apiCall("accounting/items/purchase/update", data),
  getPurchaseItem: (data) => apiCall("accounting/items/purchase/get", data),
  adjustPurchaseItemStock: (data) => apiCall("accounting/items/purchase/adjust-stock", data),
  getLowStockPurchaseItems: () => apiCall("accounting/items/purchase/low-stock", {}),
};

// ─── API KEYS (external system integration) ──────────────────────────────────
const ajaxIntegration = {
  createKey: (data) => apiCall("accounting/integration/keys/create", data),
  listKeys: (data) => apiCall("accounting/integration/keys/list", data || {}),
  revokeKey: (data) => apiCall("accounting/integration/keys/revoke", data),
};

// ─── SCHOOLPAY INTEGRATION ────────────────────────────────────────────────────
const ajaxSchoolPay = {
};

export {
  ajaxPayroll,
  ajaxFees,
  ajaxTax,
  ajaxYearClose,
  ajaxMigration,
  ajaxItems,
  ajaxSchoolPay,
  ajaxIntegration,
};
